import { useState } from "react";

import { Button, LoadingButton } from "ui/button";
import { Checkbox } from "ui/checkbox";
import { TextField } from "ui/text-field";
import { TextArea } from "ui/text-area";
import { Stack } from "ui/stack";

import { Comment, CommentReply, NewComment } from "types/comments";

interface AnnotationCommentInputProps {
  commentType?: "main" | "reply";
  parentId?: string;
  submitNewComment: (comment: NewComment) => Promise<Comment | CommentReply | void>;
  isClientUser?: boolean;
  onCancel: () => void;
  commentInputRef: React.RefObject<HTMLTextAreaElement | null>;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isDisabled?: boolean;
}

export const AnnotationCommentInput = ({
  commentType = "main",
  parentId,
  submitNewComment,
  isClientUser,
  onCancel,
  commentInputRef,
  isExpanded,
  setIsExpanded,
  isDisabled = false,
}: AnnotationCommentInputProps) => {
  const [comment, setComment] = useState<string>("");
  const [isActionItem, setIsActionItem] = useState(true);
  const [isHidden, setIsHidden] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function setInitialState() {
    setIsExpanded(false);
    setComment("");
    setIsActionItem(true);
    setIsHidden(false);
    setIsLoading(false);
  }

  function handleSubmit() {
    setIsLoading(true);

    const payload: NewComment = {
      text: comment,
    };

    if (commentType === "main") {
      payload.actionItem = !isClientUser ? isActionItem : true;
      payload.hidden = !isHidden;
    } else {
      payload.parentId = parentId;
    }

    submitNewComment(payload).finally(() => {
      setInitialState();
      setIsLoading(false);
    });
  }

  function handleCancel() {
    onCancel();
    setTimeout(() => {
      setInitialState();
    }, 100);
  }

  const isFirstComment = commentType === "main";
  const isReplyToComment = !isFirstComment;

  const inputPlaceholder = isFirstComment ? "Add a comment..." : "Add a reply...";

  return !isExpanded && isReplyToComment ? (
    <TextField
      placeholder={inputPlaceholder}
      autoComplete="off"
      onFocus={() => setIsExpanded((isExpanded) => !isExpanded)}
      data-testid="comment-input-collapsed"
      disabled={isDisabled}
    />
  ) : (
    <Stack spacing={2}>
      <TextArea
        disabled={isDisabled}
        ref={commentInputRef}
        rows={4}
        resize="none"
        placeholder={inputPlaceholder}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        data-testid="comment-input-main"
        autoComplete="off"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />
      {/* Stack spacing is 8px, not 4px: the checkbox no longer carries MUI's 9px padding. */}
      {isFirstComment && !isClientUser && (
        <Stack spacing={1}>
          <IsVisibleToClientCheckbox
            checked={isHidden}
            disabled={isDisabled}
            onChange={setIsHidden}
          />
          <IsActionItemCheckbox
            checked={isActionItem}
            disabled={isDisabled}
            onChange={setIsActionItem}
          />
        </Stack>
      )}
      <div className="flex gap-4 justify-end">
        <Button
          data-testid="comment-input-cancel-button"
          variant="outlined"
          onClick={handleCancel}>
          Cancel
        </Button>
        <LoadingButton
          data-testid="comment-input-submit-button"
          state={isLoading ? "loading" : "default"}
          loadingMessage="Submitting..."
          successMessage="Submitted"
          errorMessage="Error. Try again."
          disabled={comment === "" || isDisabled}
          onClick={handleSubmit}>
          Submit
        </LoadingButton>
      </div>
    </Stack>
  );
};

/**
 * A local shape rather than `CheckboxProps`: that type is a `label` XOR `aria-label` union, and spreading
 * a union after a fixed `label=` invites a conflict between the two branches.
 */
interface CommentFlagCheckboxProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

const IsVisibleToClientCheckbox = ({ ...rest }: CommentFlagCheckboxProps) => {
  const labelText = "Visible to client";
  return (
    <Checkbox data-testid="comment-input-checkbox-hidden" label={labelText} size="sm" {...rest} />
  );
};

const IsActionItemCheckbox = ({ ...rest }: CommentFlagCheckboxProps) => {
  const labelText = "Flag as action item";
  return (
    <Checkbox
      data-testid="comment-input-checkbox-actionitem"
      label={labelText}
      size="sm"
      {...rest}
    />
  );
};
